import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Sync as SyncIcon,
  PlayArrow as RunIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {
  getMailerliteStatus,
  syncAllMailerlite,
  resetAndResyncMailerlite,
  runTrialReminder,
  previewTrialReminder,
  runOnboardingReminder,
  previewOnboardingReminder,
  runSegmentSync,
  previewExpireSweep,
  runExpireSweep,
  previewDietaryTagBackfill,
  runDietaryTagBackfill,
  previewMailerliteExpireDead,
  runMailerliteExpireDead,
  MailerliteExpireDeadSummary,
  SegmentCounts,
  DietaryBackfillSummary,
} from '../adminApi';

type BusyKey =
  | 'mlStatus'
  | 'mlSyncAll'
  | 'mlResetResync'
  | 'segmentSync'
  | 'trialPreview'
  | 'trialRun'
  | 'onboardingPreview'
  | 'onboardingRun'
  | 'expirePreview'
  | 'expireRun'
  | 'tagBackfillPreview'
  | 'tagBackfillRun'
  | 'expireDeadPreview'
  | 'expireDeadRun';

interface PreviewUser {
  user_id: number;
  email: string;
  name?: string;
  first_name?: string;
  trial_end_date?: string;
  end_date?: string;
}

const JobsSync: React.FC = () => {
  const [busy, setBusy] = useState<Record<BusyKey, boolean>>({
    mlStatus: false,
    mlSyncAll: false,
    mlResetResync: false,
    segmentSync: false,
    trialPreview: false,
    trialRun: false,
    onboardingPreview: false,
    onboardingRun: false,
    expirePreview: false,
    expireRun: false,
    tagBackfillPreview: false,
    tagBackfillRun: false,
    expireDeadPreview: false,
    expireDeadRun: false,
  });

  const [mlConfigured, setMlConfigured] = useState<boolean | null>(null);
  const [segments, setSegments] = useState<SegmentCounts | null>(null);

  const [trialPreview, setTrialPreview] = useState<PreviewUser[] | null>(null);
  const [onboardingPreview, setOnboardingPreview] = useState<PreviewUser[] | null>(null);
  const [expirePreview, setExpirePreview] = useState<PreviewUser[] | null>(null);
  const [tagBackfill, setTagBackfill] = useState<DietaryBackfillSummary | null>(null);
  const [expireDead, setExpireDead] = useState<MailerliteExpireDeadSummary | null>(null);

  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);

  const setBusyKey = (key: BusyKey, value: boolean) =>
    setBusy((prev) => ({ ...prev, [key]: value }));

  const loadMlStatus = useCallback(async () => {
    setBusyKey('mlStatus', true);
    try {
      const res = await getMailerliteStatus();
      setMlConfigured(res.configured);
      setSegments(res.segments);
    } catch (err: any) {
      setMessage({ severity: 'error', text: `MailerLite status failed: ${err.message}` });
    } finally {
      setBusyKey('mlStatus', false);
    }
  }, []);

  useEffect(() => {
    loadMlStatus();
  }, [loadMlStatus]);

  const handleMlSyncAll = async () => {
    if (!window.confirm('Sync ALL users to MailerLite? This runs in the background and can take ~15 minutes.')) return;
    setBusyKey('mlSyncAll', true);
    try {
      const res = await syncAllMailerlite();
      setMessage({ severity: 'success', text: res.message || 'Sync started in background.' });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Sync failed: ${err.message}` });
    } finally {
      setBusyKey('mlSyncAll', false);
    }
  };

  const handleMlResetResync = async () => {
    const confirmed = window.confirm(
      'NUCLEAR RESET: Remove every subscriber from every managed segment group, then re-sync from scratch.\n\n' +
      'This eliminates all stale group memberships (e.g., users still in pre-freemium "Trial Active" / "Trial Expired" groups).\n\n' +
      'Newsletter (Snack\'d Community) and unmanaged groups (e.g., Early Access) are NOT touched.\n\n' +
      'Total runtime: ~20 minutes. Continue?'
    );
    if (!confirmed) return;
    setBusyKey('mlResetResync', true);
    try {
      const res = await resetAndResyncMailerlite();
      setMessage({ severity: 'success', text: res.message || 'Reset + resync started in background.' });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Reset failed: ${err.message}` });
    } finally {
      setBusyKey('mlResetResync', false);
    }
  };

  const handleSegmentSync = async () => {
    setBusyKey('segmentSync', true);
    try {
      const res = await runSegmentSync();
      setMessage({ severity: 'success', text: res.message || 'Segment sync started.' });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Segment sync failed: ${err.message}` });
    } finally {
      setBusyKey('segmentSync', false);
    }
  };

  const handleTrialPreview = async () => {
    setBusyKey('trialPreview', true);
    try {
      const res = await previewTrialReminder();
      setTrialPreview(res.users);
      setMessage({ severity: 'info', text: `Trial reminder preview: ${res.count} user(s) match.` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setBusyKey('trialPreview', false);
    }
  };

  const handleTrialRun = async () => {
    if (!window.confirm('Send trial-ending-soon emails NOW to all matching users?')) return;
    setBusyKey('trialRun', true);
    try {
      const res = await runTrialReminder();
      setMessage({ severity: 'success', text: `Trial reminder job ran: ${JSON.stringify(res)}` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Run failed: ${err.message}` });
    } finally {
      setBusyKey('trialRun', false);
    }
  };

  const handleOnboardingPreview = async () => {
    setBusyKey('onboardingPreview', true);
    try {
      const res = await previewOnboardingReminder();
      setOnboardingPreview(res.users);
      setMessage({ severity: 'info', text: `Onboarding reminder preview: ${res.count} user(s) match.` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setBusyKey('onboardingPreview', false);
    }
  };

  const handleOnboardingRun = async () => {
    if (!window.confirm('Send onboarding-abandonment emails NOW to all matching users?')) return;
    setBusyKey('onboardingRun', true);
    try {
      const res = await runOnboardingReminder();
      setMessage({ severity: 'success', text: `Onboarding reminder job ran: ${JSON.stringify(res)}` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Run failed: ${err.message}` });
    } finally {
      setBusyKey('onboardingRun', false);
    }
  };

  const handleExpirePreview = async () => {
    setBusyKey('expirePreview', true);
    try {
      const res = await previewExpireSweep();
      setExpirePreview(res.users);
      setMessage({ severity: 'info', text: `Expire sweep preview: ${res.count} user(s) match.` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setBusyKey('expirePreview', false);
    }
  };

  const handleExpireRun = async () => {
    if (!window.confirm('Flip all matching users from "paid" to "lapsed"? This will also resync them to MailerLite as "churned".')) return;
    setBusyKey('expireRun', true);
    try {
      const res = await runExpireSweep();
      setMessage({ severity: 'success', text: res.message });
      setExpirePreview(null);
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Run failed: ${err.message}` });
    } finally {
      setBusyKey('expireRun', false);
    }
  };

  const handleTagBackfillPreview = async () => {
    setBusyKey('tagBackfillPreview', true);
    try {
      const res = await previewDietaryTagBackfill();
      setTagBackfill(res);
      setMessage({
        severity: 'info',
        text: `Backfill preview: ${res.pendingCount} tag(s) would be added across ${res.recipesProcessed} recipe(s).`,
      });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setBusyKey('tagBackfillPreview', false);
    }
  };

  const handleTagBackfillRun = async () => {
    const pending = tagBackfill?.pendingCount;
    const confirmMsg = pending
      ? `Insert ${pending} recipetags rows now? This adds tags only — never removes existing ones. Safe to re-run.`
      : `Run the dietary tag backfill? This adds tags only (never removes) and is safe to re-run.`;
    if (!window.confirm(confirmMsg)) return;
    setBusyKey('tagBackfillRun', true);
    try {
      const res = await runDietaryTagBackfill();
      setTagBackfill(res);
      setMessage({ severity: 'success', text: res.message || `Inserted ${res.inserted} rows.` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Run failed: ${err.message}` });
    } finally {
      setBusyKey('tagBackfillRun', false);
    }
  };

  const handleExpireDeadPreview = async () => {
    setBusyKey('expireDeadPreview', true);
    try {
      const res = await previewMailerliteExpireDead();
      setExpireDead(res);
      setMessage({
        severity: 'info',
        text: `Expire-dead preview: ${res.candidates} user(s) would be deleted from MailerLite + flagged marketing_opt_in=false.`,
      });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setBusyKey('expireDeadPreview', false);
    }
  };

  const handleExpireDeadRun = async () => {
    const candidates = expireDead?.candidates;
    const confirmMsg = candidates
      ? `Permanently expire ${candidates} dead subscriber(s)? This deletes them from MailerLite AND flags marketing_opt_in=false in the DB. Reversible per-user (flip the column back) but safe default.`
      : `Run the mailerlite expire-dead sweep? Flags users as opted out and deletes them from MailerLite. Safe to re-run.`;
    if (!window.confirm(confirmMsg)) return;
    setBusyKey('expireDeadRun', true);
    try {
      const res = await runMailerliteExpireDead();
      setExpireDead(res);
      setMessage({ severity: 'success', text: res.message || `Expired ${res.expiredCount} user(s).` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Run failed: ${err.message}` });
    } finally {
      setBusyKey('expireDeadRun', false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Jobs & Sync</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manually trigger cron jobs and MailerLite sync operations. Long-running jobs continue in the background after the response.
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* MailerLite */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6">MailerLite</Typography>
          {mlConfigured === true && <Chip size="small" color="success" icon={<CheckIcon />} label="Configured" />}
          {mlConfigured === false && <Chip size="small" color="warning" label="Not configured" />}
        </Stack>

        {segments && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Segment counts (current DB state)</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(segments).map(([name, count]) => (
                <Chip key={name} size="small" label={`${name}: ${count}`} />
              ))}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={busy.mlStatus ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={loadMlStatus}
            disabled={busy.mlStatus}
          >
            Refresh status
          </Button>
          <Button
            variant="contained"
            startIcon={busy.mlSyncAll ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleMlSyncAll}
            disabled={busy.mlSyncAll || mlConfigured === false}
          >
            Sync all users to MailerLite
          </Button>
          <Button
            variant="outlined"
            startIcon={busy.segmentSync ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSegmentSync}
            disabled={busy.segmentSync}
          >
            Run daily segment sync
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={busy.mlResetResync ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleMlResetResync}
            disabled={busy.mlResetResync || mlConfigured === false}
          >
            Nuclear reset + resync
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Nuclear reset: removes every subscriber from every managed segment group, then re-syncs from scratch. Use to fix stale group memberships from pre-freemium syncs. ~20 min total.
        </Typography>
      </Paper>

      {/* Trial reminder */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Trial Reminder Job</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends "trial ending in 2 days" emails to paid users with trial_end_date 2 days out, then runs the post-trial expired survey for users now in 'lapsed'.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={busy.trialPreview ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={handleTrialPreview}
            disabled={busy.trialPreview}
          >
            Preview matches
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={busy.trialRun ? <CircularProgress size={16} /> : <RunIcon />}
            onClick={handleTrialRun}
            disabled={busy.trialRun}
          >
            Run job now
          </Button>
        </Stack>
        {trialPreview && (
          <PreviewTable rows={trialPreview} extraColumn="trial_end_date" />
        )}
      </Paper>

      {/* Defensive expire sweep */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Expire Sweep (Lapse Past-Due Paid Users)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Flips users with status='paid', auto_renew=false, and end_date in the past to status='lapsed'.
          Catches legacy backend-trial users (no webhook) and any missed webhook edge cases.
          Runs daily via cron at 14:00 UTC. Re-syncs flipped users to MailerLite as 'churned'.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={busy.expirePreview ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={handleExpirePreview}
            disabled={busy.expirePreview}
          >
            Preview matches
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={busy.expireRun ? <CircularProgress size={16} /> : <RunIcon />}
            onClick={handleExpireRun}
            disabled={busy.expireRun}
          >
            Run sweep now
          </Button>
        </Stack>
        {expirePreview && (
          <PreviewTable rows={expirePreview} extraColumn="end_date" />
        )}
      </Paper>

      {/* Onboarding reminder */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Onboarding Reminder Job</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends abandonment emails to users in 'onboarding' for 48h-14d who haven't received one.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={busy.onboardingPreview ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={handleOnboardingPreview}
            disabled={busy.onboardingPreview}
          >
            Preview matches
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={busy.onboardingRun ? <CircularProgress size={16} /> : <RunIcon />}
            onClick={handleOnboardingRun}
            disabled={busy.onboardingRun}
          >
            Run job now
          </Button>
        </Stack>
        {onboardingPreview && (
          <PreviewTable rows={onboardingPreview} />
        )}
      </Paper>

      {/* Dietary tag backfill — deterministic, no email sends, add-only */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Dietary Tag Backfill</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Adds missing dietary/allergen/nutrition tags to complete recipes based on ingredient patterns (no-pork, no-red-meat, vegetarian, vegan, pescatarian, dairy-free, nut-free, gluten-free, etc.) and nutrition thresholds (low-carb, low-sodium, heart-healthy, etc.). Deterministic — no AI calls. Never removes tags. Safe to re-run.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={busy.tagBackfillPreview ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={handleTagBackfillPreview}
            disabled={busy.tagBackfillPreview || busy.tagBackfillRun}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={busy.tagBackfillRun ? <CircularProgress size={16} /> : <RunIcon />}
            onClick={handleTagBackfillRun}
            disabled={busy.tagBackfillRun || busy.tagBackfillPreview}
          >
            Run backfill
          </Button>
        </Stack>

        {tagBackfill && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Chip label={`${tagBackfill.recipesProcessed} recipes processed`} />
              <Chip
                label={
                  tagBackfill.inserted !== undefined
                    ? `${tagBackfill.inserted} inserted`
                    : `${tagBackfill.pendingCount} pending`
                }
                color={tagBackfill.inserted !== undefined ? 'success' : 'primary'}
                icon={tagBackfill.inserted !== undefined ? <CheckIcon /> : undefined}
              />
              {tagBackfill.missingTagNames.length > 0 && (
                <Chip
                  label={`${tagBackfill.missingTagNames.length} tag(s) missing from DB`}
                  color="warning"
                />
              )}
            </Stack>

            {Object.entries(tagBackfill.gainedByTag).filter(([, n]) => n > 0).length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Tag additions</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell align="right">Recipes gaining it</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(tagBackfill.gainedByTag)
                        .filter(([, n]) => n > 0)
                        .sort((a, b) => b[1] - a[1])
                        .map(([tag, count]) => (
                          <TableRow key={tag}>
                            <TableCell>{tag}</TableCell>
                            <TableCell align="right">{count}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {tagBackfill.sampleRecipes.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Sample recipes (first {tagBackfill.sampleRecipes.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Recipe</TableCell>
                        <TableCell>Tags added</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tagBackfill.sampleRecipes.map((r) => (
                        <TableRow key={r.recipe_id}>
                          <TableCell>{r.recipe_id}</TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {r.added.map((t) => (
                                <Chip key={t} label={t} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* MailerLite expire-dead — permanent-churn cleanup. Nightly cron + manual trigger. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>MailerLite Expire-Dead (keep subscriber count under 500)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Finds users who are deeply churned (pending_migration never-logged-in &gt; 60d, lapsed/expired inactive &gt; 60d, legacy active past end_date &gt; 90d), deletes them from MailerLite via GDPR forget, and flags <code>marketing_opt_in = false</code> in the DB so the nightly segment sync never re-adds them. Runs automatically at 04:00 UTC daily; this is the manual trigger. Reversible per-user by flipping <code>marketing_opt_in</code> back to true.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={busy.expireDeadPreview ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={handleExpireDeadPreview}
            disabled={busy.expireDeadPreview || busy.expireDeadRun}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={busy.expireDeadRun ? <CircularProgress size={16} /> : <RunIcon />}
            onClick={handleExpireDeadRun}
            disabled={busy.expireDeadRun || busy.expireDeadPreview}
          >
            Expire now
          </Button>
        </Stack>

        {expireDead && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Chip label={`${expireDead.candidates} candidate(s)`} />
              {!expireDead.dryRun && (
                <Chip
                  label={`${expireDead.expiredCount} expired`}
                  color="success"
                  icon={<CheckIcon />}
                />
              )}
              {expireDead.mailerliteErrors > 0 && (
                <Chip
                  label={`${expireDead.mailerliteErrors} MailerLite error(s)`}
                  color="warning"
                />
              )}
              {expireDead.dbErrors > 0 && (
                <Chip label={`${expireDead.dbErrors} DB error(s)`} color="error" />
              )}
            </Stack>

            {expireDead.sample.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Sample (first {expireDead.sample.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User ID</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Days inactive</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expireDead.sample.map((u) => (
                        <TableRow key={u.user_id}>
                          <TableCell>{u.user_id}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{u.status || '—'}</TableCell>
                          <TableCell align="right">
                            {u.days_inactive === null ? 'never' : u.days_inactive}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        Tip: Preview before running. All "Run" actions trigger real email sends.
      </Typography>
    </Box>
  );
};

const PreviewTable: React.FC<{ rows: PreviewUser[]; extraColumn?: 'trial_end_date' | 'end_date' }> = ({ rows, extraColumn }) => {
  if (rows.length === 0) {
    return <Alert severity="info">No matching users.</Alert>;
  }
  const extraLabel = extraColumn === 'trial_end_date' ? 'Trial Ends' : extraColumn === 'end_date' ? 'End Date' : null;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User ID</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Name</TableCell>
            {extraLabel && <TableCell>{extraLabel}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((u) => {
            const extraVal = extraColumn === 'trial_end_date' ? u.trial_end_date : extraColumn === 'end_date' ? u.end_date : null;
            return (
              <TableRow key={u.user_id}>
                <TableCell>{u.user_id}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.name || u.first_name || '—'}</TableCell>
                {extraLabel && (
                  <TableCell>{extraVal ? new Date(extraVal).toLocaleString() : '—'}</TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default JobsSync;
